import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injector, InjectionToken } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { catchError, filter, from, of, switchMap, take, throwError } from 'rxjs';
import { ToastNotificationService } from '../../atoms/toast-notification/toast-notification.service';

import { firebaseAuth } from '../firebase/firebase';
import { environment } from '../../../environments/environment';

/**
 * InjectionToken wrapping the Firebase Auth instance.
 * Using DI avoids a circular dependency (FirebaseService → HttpClient → interceptor)
 * and makes the interceptor testable via TestBed without module-level vi.mock.
 */
export const FIREBASE_AUTH_TOKEN = new InjectionToken<typeof firebaseAuth>('FIREBASE_AUTH_TOKEN', {
  providedIn: 'root',
  factory: () => firebaseAuth,
});

// Marks a retried request so the 401 handler does not re-enter the refresh cycle
export const SKIP_AUTH_RETRY = new HttpContextToken<boolean>(() => false);

// Module-level mutex — prevents concurrent 401s from triggering multiple refreshes
let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Resets module-level state. Export is for test isolation only — not part of public API.
 * Call in beforeEach alongside TestBed.resetTestingModule().
 */
export function resetInterceptorState(): void {
  isRefreshing = false;
  refreshTokenSubject = new BehaviorSubject<string | null>(null);
}

/**
 * A 401/403 with no Firebase user cannot refresh or resync. Propagate the original
 * HTTP error so callers see a normal status instead of an unhandled generic Error.
 */
function failRefreshWithoutUser(error: HttpErrorResponse) {
  isRefreshing = false;
  refreshTokenSubject.error(error);
  refreshTokenSubject = new BehaviorSubject<string | null>(null);
  return throwError(() => error);
}

/** Server-enforced sandbox blocks are not stale-claims 403s — do not resync/retry/toast them. */
export function isSandboxRestrictionError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }
  const raw = error.error?.message;
  const text = Array.isArray(raw) ? raw.join(' ') : String(raw ?? '');
  return text.includes('Sandbox user accounts cannot');
}

// Only attach auth headers to known internal API origins.
// Third-party calls (S3 pre-signed URLs, SerpAPI, Noodle) must pass through unmodified.
const internalOrigins = [
  environment.shout_api.url,
  environment.unitaskr_api.url,
  environment.uniUser.uniUserUrl,
  environment.superSocial.url,
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(FIREBASE_AUTH_TOKEN);
  const toastService = inject(ToastNotificationService);
  // HttpClient is resolved lazily (only when a 403 fires) via the Injector to keep this
  // interceptor decoupled from FirebaseService — see FIREBASE_AUTH_TOKEN note above.
  const injector = inject(Injector);

  if (!internalOrigins.some(origin => req.url.startsWith(origin))) {
    return next(req);
  }

  return from(
    auth.currentUser?.getIdToken(false) ?? Promise.resolve(null)
  ).pipe(
    switchMap(token => {
      const authReq = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;

      return next(authReq).pipe(
        catchError(error => {
          if (error.status === 401 && !req.context.get(SKIP_AUTH_RETRY)) {
            return handle401(req, next, auth, error);
          }
          if (error.status === 403 && isSandboxRestrictionError(error)) {
            return throwError(() => error);
          }
          if (error.status === 403 && !req.context.get(SKIP_AUTH_RETRY)) {
            // A 403 often means the ID token carries stale Firebase custom claims
            // (e.g. a businessAccountId granted after this token was minted). Resync
            // claims, force-refresh the token, and retry the request exactly once.
            return handle403(req, next, auth, injector, error);
          }
          if (error.status === 403) {
            // Already retried with fresh claims and still denied — a genuine
            // authorization failure. Surface it and stop.
            void toastService.alert({
              title: 'Access denied',
              message: 'You don\'t have permission to access this resource.',
              type: 'error',
              icon: 'error',
            });
            return throwError(() => error);
          }
          return throwError(() => error);
        })
      );
    })
  );
};

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: typeof firebaseAuth,
  error: HttpErrorResponse,
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const user = auth.currentUser;
    if (!user) {
      return failRefreshWithoutUser(error);
    }

    return from(user.getIdToken(true)).pipe(
      switchMap(newToken => {
        // Reset flag BEFORE emitting so new 401s that arrive between next() and
        // the retry response don't queue on a subject that's already been consumed.
        isRefreshing = false;
        refreshTokenSubject.next(newToken);

        return next(
          req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` },
            context: req.context.set(SKIP_AUTH_RETRY, true),
          })
        );
      }),
      catchError(err => {
        // On refresh failure: reset state and re-create subject so waiting
        // subscribers (via the else branch below) receive the error cleanly.
        isRefreshing = false;
        refreshTokenSubject.error(err);
        refreshTokenSubject = new BehaviorSubject<string | null>(null);
        return throwError(() => err);
      })
    );
  }

  // Another refresh is already in flight — queue on the subject until the token arrives
  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap(token =>
      next(
        req.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
          context: req.context.set(SKIP_AUTH_RETRY, true),
        })
      )
    )
  );
}

/**
 * Recovers from a stale-claims 403. Unlike handle401 (which only force-refreshes the
 * token), this first POSTs /auth/sync-claims to make the backend recompute the caller's
 * Firebase custom claims from the DB (mirrors FirebaseService.syncClaimsAndRefreshToken),
 * then force-refreshes the token, then retries the request once with SKIP_AUTH_RETRY set.
 * The sync request itself carries SKIP_AUTH_RETRY so it can never re-enter this handler.
 * If the resynced retry still 403s, the main interceptor surfaces the "Access denied"
 * toast. Shares the 401 refresh mutex so concurrent failures trigger at most one resync.
 */
function handle403(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: typeof firebaseAuth,
  injector: Injector,
  error: HttpErrorResponse,
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const user = auth.currentUser;
    if (!user) {
      return failRefreshWithoutUser(error);
    }

    const http = injector.get(HttpClient);
    const syncUrl = `${environment.shout_api.url}/auth/sync-claims`;

    return http
      .post(syncUrl, {}, { context: new HttpContext().set(SKIP_AUTH_RETRY, true) })
      .pipe(
        take(1),
        // Non-fatal: even if the resync request fails (network/rate-limit), still
        // force-refresh the token in case the claims were already written server-side.
        catchError(() => of(null)),
        switchMap(() => from(user.getIdToken(true))),
        switchMap(newToken => {
          // Reset flag BEFORE emitting (see handle401 for rationale).
          isRefreshing = false;
          refreshTokenSubject.next(newToken);

          return next(
            req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` },
              context: req.context.set(SKIP_AUTH_RETRY, true),
            })
          );
        }),
        catchError(err => {
          isRefreshing = false;
          refreshTokenSubject.error(err);
          refreshTokenSubject = new BehaviorSubject<string | null>(null);
          return throwError(() => err);
        })
      );
  }

  // Another refresh/resync is already in flight — queue on the subject until the token arrives
  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap(token =>
      next(
        req.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
          context: req.context.set(SKIP_AUTH_RETRY, true),
        })
      )
    )
  );
}
